import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { stylesForType, styleAfterTypeChange } from '../artistStyleUtils';

// Password-manager opt-out (2026-10-09): name-type inputs carry
// autoComplete="off" plus data-1p-ignore (1Password), data-lpignore
// (LastPass), data-bwignore (Bitwarden) and data-form-type="other"
// (Dashlane) — 1Password ignores autoComplete="off" on its own.

function EditArtist() {
    const isMobile = useIsMobile();
    const { slug } = useParams();
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [artistId, setArtistId] = useState(null);
    const [artistSlug, setArtistSlug] = useState(slug);
    // SHARC ID: no longer editable here (moved to Admin Portal → Manage SHARC IDs).
    // Still fetched and round-tripped unchanged on save so an edit to other
    // fields doesn't clobber an existing admin-assigned SHARC ID.
    const [sharcId, setSharcId] = useState('');

    const [form, setForm] = useState({
        name: '',
        city: '',
        country_id: '',
        state_id: '',
        artist_type_id: '',
        artist_style_id: '',
        description: '',
        image_url: '',
    });

    const [formerlyKnownAs, setFormerlyKnownAs] = useState([]); // [{ name }]
    const [hometowns, setHometowns] = useState([]);             // [{ city, country_id, state_id }]
    const [links, setLinks] = useState([]);                     // [{ description, url }]

    useEffect(() => {
        Promise.all([
            axios.get(`${config.API_URL}/artists/${slug}`),
            axios.get(`${config.API_URL}/picklists`)
        ]).then(([artistRes, picklistsRes]) => {
            const artist = artistRes.data.artist;
            setArtistId(artist.id);
            setArtistSlug(artist.slug || slug);
            setSharcId(artist.sharc_id || '');
            setForm({
                name: artist.name || '',
                city: artist.city || '',
                country_id: artist.country_id || '',
                state_id: artist.state_id || '',
                artist_type_id: artist.artist_type_id || '',
                artist_style_id: artist.artist_style_id || '',
                description: artist.description || '',
                image_url: artist.image_url || '',
            });
            setFormerlyKnownAs(artistRes.data.formerly_known_as || []);
            setHometowns(artistRes.data.hometowns || []);
            setLinks(artistRes.data.links || []);
            setPicklists(picklistsRes.data);
            setLoading(false);
        }).catch(() => {
            setError('Failed to load artist data');
            setLoading(false);
        });
    }, [slug]);

    // Main form field change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country_id' ? { state_id: '' } : {}),
            // Dependent picklist (2026-10-12): drop a style the new type can't use
            ...(name === 'artist_type_id' && picklists
                ? { artist_style_id: styleAfterTypeChange(picklists.artist_styles, picklists.artist_types, value, prev.artist_style_id) }
                : {}),
        }));
    };

    // Styles offered for the chosen type. A legacy record whose saved style
    // doesn't fit its type keeps that style listed (flagged) so the dropdown
    // shows the real value; updateArtist only validates when type/style change.
    const typeStyles = (() => {
        if (!picklists) return [];
        const list = stylesForType(picklists.artist_styles, picklists.artist_types, form.artist_type_id);
        const current = form.artist_style_id ? parseInt(form.artist_style_id) : null;
        if (current && !list.some(st => st.id === current)) {
            const saved = picklists.artist_styles.find(st => st.id === current);
            if (saved) return [...list, { ...saved, value: `${saved.value} (doesn't match type)` }];
        }
        return list;
    })();

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    // --- Formerly Known As ---
    const addFka = () => setFormerlyKnownAs(prev => [...prev, { name: '' }]);
    const updateFka = (index, value) => {
        setFormerlyKnownAs(prev => prev.map((f, i) => i === index ? { ...f, name: value } : f));
    };
    const removeFka = (index) => setFormerlyKnownAs(prev => prev.filter((_, i) => i !== index));

    // --- Hometowns ---
    const addHometown = () => setHometowns(prev => [...prev, { city: '', country_id: '', state_id: '' }]);
    const updateHometown = (index, field, value) => {
        setHometowns(prev => prev.map((ht, i) => {
            if (i !== index) return ht;
            return { ...ht, [field]: value, ...(field === 'country_id' ? { state_id: '' } : {}) };
        }));
    };
    const removeHometown = (index) => setHometowns(prev => prev.filter((_, i) => i !== index));

    const getFilteredStatesForHometown = (country_id) => {
        if (!picklists || !country_id) return [];
        return picklists.states_regions.filter(s => s.country_id === parseInt(country_id));
    };

    // --- Links ---
    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (index, field, value) => {
        setLinks(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
    };
    const removeLink = (index) => setLinks(prev => prev.filter((_, i) => i !== index));

    // --- Save ---
    const handleSubmit = () => {
        if (!form.name) {
            setError('Artist name is required');
            return;
        }
        setError(null);
        setShowConfirm(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setDupWarning(null);
        setShowConfirm(false);
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            const payload = {
                name: form.name,
                city: form.city || null,
                country_id: form.country_id ? parseInt(form.country_id) : null,
                state_id: form.state_id ? parseInt(form.state_id) : null,
                artist_type_id: form.artist_type_id ? parseInt(form.artist_type_id) : null,
                artist_style_id: form.artist_style_id ? parseInt(form.artist_style_id) : null,
                description: form.description || null,
                image_url: form.image_url || null,
                sharc_id: sharcId.trim() || null,
                formerly_known_as: formerlyKnownAs.filter(f => f.name.trim()),
                hometowns: hometowns
                    .filter(ht => ht.city || ht.state_id || ht.country_id)
                    .map(ht => ({
                        city: ht.city || null,
                        state_id: ht.state_id ? parseInt(ht.state_id) : null,
                        country_id: ht.country_id ? parseInt(ht.country_id) : null,
                    })),
                links: links.filter(l => l.description.trim() && l.url.trim()),
            };
            const res = await axios.put(`${config.API_URL}/artists/${artistId}`, payload, { headers: { Authorization: idToken } });
            const newSlug = res.data.slug || artistSlug;
            navigate(`/artists/${newSlug}`);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else if (err.response?.data?.error === 'invalid_style') {
                setError(err.response.data.message);
            } else {
                setError('Failed to save changes');
            }
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to={`/artists/${artistSlug}`} style={styles.backLink}>← Back to Artist</Link>
            <h1 style={styles.title}>Edit Artist</h1>

            {error && <div style={styles.errorBox}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ Duplicate artist detected.</strong> Another artist with this name, city, and location already exists:
                    <div style={{ marginTop: '8px' }}>
                        <strong>{dupWarning.name}</strong>
                        {(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && (
                            <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>
                        )}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <a href={`/artists/${dupWarning.id}`} style={styles.dupLink}>→ View existing artist</a>
                    </div>
                </div>
            )}

            <div style={styles.form}>

                {/* Basic Info */}
                <div style={styles.sectionHeader}>Basic Info</div>

                <div style={styles.field}>
                    <label style={styles.label}>Artist Name *</label>
                    <input autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" data-form-type="other" name="name" value={form.name} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} style={styles.input} />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Country</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select country...</option>
                        {picklists.countries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {form.country_id && filteredStates.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>State / Region</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select state/region...</option>
                            {filteredStates.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={isMobile ? { ...styles.twoCol, ...Smobile.twoCol } : styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Artist Type</label>
                        <select name="artist_type_id" value={form.artist_type_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select type...</option>
                            {picklists.artist_types.map(t => (
                                <option key={t.id} value={t.id}>{t.value}</option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Artist Style</label>
                        <select name="artist_style_id" value={form.artist_style_id} onChange={handleChange} disabled={!form.artist_type_id && !form.artist_style_id} style={form.artist_type_id || form.artist_style_id ? styles.input : { ...styles.input, opacity: 0.5, cursor: 'not-allowed' }}>
                            <option value="">{form.artist_type_id ? 'Select style...' : 'Select an Artist Type first'}</option>
                            {typeStyles.map(s => (
                                <option key={s.id} value={s.id}>{s.value}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Bio & Image */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Bio &amp; Image</div>

                <div style={styles.field}>
                    <label style={styles.label}>Bio</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder="A short description of the artist..."
                        style={{ ...styles.input, resize: 'vertical', lineHeight: '1.6' }}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input
                        name="image_url"
                        value={form.image_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        style={styles.input}
                    />
                    {form.image_url && (
                        <div style={styles.imagePreview}>
                            <img
                                src={form.image_url}
                                alt="Preview"
                                style={styles.previewImg}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                </div>

                {/* Formerly Known As */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Formerly Known As</div>

                {formerlyKnownAs.map((fka, index) => (
                    <div key={index} style={styles.repeatRow}>
                        <input autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" data-form-type="other"
                            value={fka.name}
                            onChange={e => updateFka(index, e.target.value)}
                            placeholder="Former name..."
                            style={{ ...styles.input, flex: 1 }}
                        />
                        <button onClick={() => removeFka(index)} style={styles.removeButton}>✕</button>
                    </div>
                ))}
                <button onClick={addFka} style={styles.addRowButton}>+ Add Name</button>

                {/* Hometowns */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Hometowns</div>
                <div style={styles.sectionHint}>Additional cities this artist is associated with.</div>

                {hometowns.map((ht, index) => {
                    const htStates = getFilteredStatesForHometown(ht.country_id);
                    return (
                        <div key={index} style={styles.repeatCard}>
                            <div style={isMobile ? { ...styles.repeatCardRow, ...Smobile.repeatCardRow } : styles.repeatCardRow}>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.miniLabel}>City</label>
                                    <input
                                        value={ht.city}
                                        onChange={e => updateHometown(index, 'city', e.target.value)}
                                        placeholder="City..."
                                        style={styles.input}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={styles.miniLabel}>Country</label>
                                    <select
                                        value={ht.country_id}
                                        onChange={e => updateHometown(index, 'country_id', e.target.value)}
                                        style={styles.input}
                                    >
                                        <option value="">Select...</option>
                                        {picklists.countries.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                {ht.country_id && htStates.length > 0 && (
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.miniLabel}>State / Region</label>
                                        <select
                                            value={ht.state_id}
                                            onChange={e => updateHometown(index, 'state_id', e.target.value)}
                                            style={styles.input}
                                        >
                                            <option value="">Select...</option>
                                            {htStates.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <button onClick={() => removeHometown(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                            </div>
                        </div>
                    );
                })}
                <button onClick={addHometown} style={styles.addRowButton}>+ Add Hometown</button>

                {/* Links */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Links</div>
                <div style={styles.sectionHint}>Website, Bandcamp, Spotify, social media, etc.</div>

                {links.map((link, index) => (
                    <div key={index} style={styles.repeatCard}>
                        <div style={isMobile ? { ...styles.repeatCardRow, ...Smobile.repeatCardRow } : styles.repeatCardRow}>
                            <div style={isMobile ? { width: '100%' } : { flex: '0 0 160px' }}>
                                <label style={styles.miniLabel}>Label</label>
                                <input
                                    value={link.description}
                                    onChange={e => updateLink(index, 'description', e.target.value)}
                                    placeholder="e.g. Bandcamp"
                                    style={styles.input}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>URL</label>
                                <input
                                    value={link.url}
                                    onChange={e => updateLink(index, 'url', e.target.value)}
                                    placeholder="https://..."
                                    style={styles.input}
                                />
                            </div>
                            <button onClick={() => removeLink(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                        </div>
                    </div>
                ))}
                <button onClick={addLink} style={styles.addRowButton}>+ Add Link</button>

                {/* Save / Cancel */}
                <div style={{ ...styles.sectionDivider, marginBottom: '24px' }} />
                <div style={styles.buttonRow}>
                    <button onClick={() => navigate(`/artists/${artistSlug}`)} style={styles.cancelButton}>Cancel</button>
                    <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirm && (
                <div style={styles.overlay}>
                    <div style={styles.modal}>
                        <h2 style={styles.modalTitle}>Confirm Changes</h2>
                        <p style={styles.modalText}>
                            Are you sure you want to save changes to <strong style={{ color: '#bb86fc' }}>{form.name}</strong>?
                        </p>
                        <div style={styles.modalButtons}>
                            <button onClick={() => setShowConfirm(false)} style={styles.cancelButton}>Go Back</button>
                            <button onClick={handleSave} style={styles.submitButton}>Yes, Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: '680px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
    sectionDivider: { borderTop: '1px solid #2a2a2a', margin: '28px 0 24px' },
    sectionHint: { fontSize: '13px', color: '#666', marginBottom: '14px', marginTop: '-10px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', color: '#aaa', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    miniLabel: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    imagePreview: { marginTop: '10px' },
    previewImg: { width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #333' },
    repeatRow: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' },
    repeatCard: { backgroundColor: '#252525', border: '1px solid #333', borderRadius: '6px', padding: '14px', marginBottom: '10px' },
    repeatCardRow: { display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' },
    addRowButton: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px dashed #555', borderRadius: '4px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' },
    removeButton: { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '4px', padding: '8px 10px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 },
    buttonRow: { display: 'flex', gap: '12px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', flex: 1 },
    cancelButton: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', cursor: 'pointer', flex: 1 },
    errorBox: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '12px', padding: '32px', maxWidth: '440px', width: '90%' },
    modalTitle: { fontSize: '22px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '16px' },
    modalText: { fontSize: '16px', color: '#aaa', marginBottom: '28px', lineHeight: '1.5' },
    modalButtons: { display: 'flex', gap: '12px' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.7) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
//
// repeatCardRow added retroactively (Step 17 follow-up): the Hometowns and
// Links repeater rows use an inline flex row rather than gridTemplateColumns,
// so the original grep-based audit didn't catch them. Links specifically had
// a fixed flex: '0 0 160px' label column that squeezed the URL field to
// ~100px on mobile — found while auditing AddVenue.js/EditVenue.js, which
// have the identical pattern.
const Smobile = {
    twoCol: {
        gridTemplateColumns: '1fr',
    },
    repeatCardRow: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
    },
};

export default EditArtist;